
import { defineComponent, computed, ref, PropType, onMounted, onBeforeUnmount, watch } from 'vue';
import Card from 'primevue/card';
import ProgressSpinner from 'primevue/progressspinner';
import { getBankBgClass, getBankImage, getBankCodeByName } from '../../utils/bank';
import { useToast } from 'primevue/usetoast';
import { formatCurrency } from '../../utils/currency';

type BankInfo = {
	fullName: string;
	bankName: string;
	bankNumber: string;
	bankCode: string;
};

export default defineComponent({
	name: 'BankDetail',
	emits: {
		expired: () => true,
		tick: (v: number) => typeof v === 'number',
	},
	components: {
		Card,
		ProgressSpinner,
	},
	props: {
		/** parent เปิดใช้งานบัตรนี้หรือยัง */
		isActivate: { type: Boolean, default: true },
		/** แสดงปุ่มคัดลอกเลขบัญชี */
		isCopyNumber: { type: Boolean, default: false },
		/** ข้อมูลบัญชีปลายทาง */
		bank: {
			type: Object as PropType<BankInfo>,
			default: () => ({ fullName: '', bankName: '', bankNumber: '', bankCode: '' }),
		},
		/** จำนวนเงินที่ต้องโอน (เพื่อแสดง) */
		showAmount: { type: Number, default: 0 },
		/** เวลาที่เหลือ (วินาที) จาก parent */
		expireTime: { type: Number, default: 0 },
		/** สถานะโหลดจากภายนอก */
		loading: { type: Boolean, default: false },
	},
	setup(props, { emit }) {
		const toast = useToast();

		/** loading ตาม prop */
		const isLoading = computed(() => props.loading);
		/** สะดวกเวลา access field และช่วยให้ type inference ดี */
		const bank = computed(() => props.bank);
		// resolve ชื่อธนาคาร -> โค้ด ก่อน lookup (BO อาจส่งชื่อไทย ไม่ใช่โค้ด) กัน BG/logo หาย
		const bankKey = computed(() => getBankCodeByName(bank.value.bankName || bank.value.bankCode || ''));

		/** พร้อมแสดงบัตรบัญชี */
		const isActive = computed(
			() =>
				props.isActivate ||
				(!!bank.value.bankNumber &&
					!!bank.value.bankCode &&
					!!bank.value.bankName &&
					!!bank.value.fullName)
		);

		/** คัดลอกเลขบัญชี */
		const copied = ref(false);
		const copyToClipboard = async () => {
			try {
				const raw = bank.value.bankNumber ?? '';
				const digitsOnly = raw.replace(/[^\d]/g, '');
				if (!digitsOnly) {
					toast?.add?.({ severity: 'warn', summary: 'ไม่มีเลขบัญชี', detail: 'ไม่พบเลขบัญชีให้คัดลอก', life: 2000 });
					return;
				}

				if (navigator.clipboard?.writeText) {
					await navigator.clipboard.writeText(digitsOnly);
				} else {
					// fallback
					const ta = document.createElement('textarea');
					ta.value = digitsOnly;
					ta.setAttribute('readonly', 'true');
					ta.style.position = 'fixed';
					ta.style.opacity = '0';
					document.body.appendChild(ta);
					ta.select();
					document.execCommand('copy');
					document.body.removeChild(ta);
				}

				copied.value = true;
				setTimeout(() => (copied.value = false), 1000);
				toast?.add?.({ severity: 'success', summary: 'คัดลอกเลขบัญชี', detail: 'คัดลอกเรียบร้อย', life: 1500 });
			} catch (error: unknown) {
				const msg = error instanceof Error ? error.message : String(error);
				toast?.add?.({ severity: 'error', summary: 'คัดลอกไม่สำเร็จ', detail: msg, life: 3000 });
			}
		};

		/** นับถอยหลัง */
		const remaining = ref<number>(0);
		let timer: number | null = null;
		const started = ref(false); // เคยเริ่มนับจริงหรือยัง
		const expired = ref(false); // หมดเวลาแล้วหรือยัง (หลังจากเริ่ม)

		const stopCountdown = () => {
			if (timer !== null) {
				window.clearInterval(timer);
				timer = null;
			}
		};

		/** เริ่มได้เมื่อ active และ expireTime > 0 */
		const canStart = computed(() => props.isActivate && props.expireTime > 0);

		const tick = () => {
			remaining.value = Math.max(0, remaining.value - 1);
			emit('tick', remaining.value);

			if (remaining.value === 0) {
				stopCountdown();
				if (started.value && !expired.value) {
					expired.value = true;
					toast?.add?.({
						severity: 'warn',
						summary: 'หมดเวลา',
						detail: 'รายการหมดเวลา กรุณาสร้างใหม่',
						life: 2500,
					});
					emit('expired');
				}
			}
		};

		const startCountdown = () => {
			stopCountdown();
			// รีเซ็ตสถานะรอบใหม่
			expired.value = false;
			started.value = false;

			if (!canStart.value) {
				// ยังไม่พร้อมเริ่ม: แค่ sync remaining จาก prop
				remaining.value = Math.max(0, props.expireTime);
				return;
			}

			started.value = true;
			remaining.value = Math.max(0, props.expireTime);
			emit('tick', remaining.value); // sync ค่าเริ่มกับ parent

			if (remaining.value > 0) {
				timer = window.setInterval(tick, 1000);
			} else {
				// กรณีเริ่มด้วย 0 และถือว่าเริ่มจริง → ถือว่าหมดเวลา
				expired.value = true;
				emit('expired');
			}
		};

		onMounted(() => {
			remaining.value = Math.max(0, props.expireTime);
			startCountdown();
		});

		onBeforeUnmount(() => stopCountdown());

		/** เปลี่ยน expireTime หรือ isActivate → ประเมินใหม่ */
		watch([() => props.expireTime, () => props.isActivate], () => {
			startCountdown();
		});

		/** ถ้าเปลี่ยน isActive (คำนิยามกว้างกว่า) ตอนเหลือ > 0 และยังไม่เริ่ม ให้เริ่ม */
		watch(isActive, (active) => {
			if (active && remaining.value > 0 && !started.value) {
				startCountdown();
			}
		});

		return {
			// utils
			getBankBgClass,
			getBankImage,
			bankKey,
			formatCurrency,

			// state
			isLoading,
			isActive,
			bank,
			copied,
			copyToClipboard,
			remaining,
			expired,
		};
	},
});
